import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import { SiteSearchSelect } from '@/components/SiteSearchSelect';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { useSafeAccess } from '@/lib/access';
import { useSites } from '@/lib/queries';
import { errorMessage, inputStyle } from '@/lib/format';

import { EducationTemplateEditor } from './EducationTemplateEditor';
import { PatrolTemplateEditor } from './PatrolTemplateEditor';
import { SafeJournalEditor } from './SafeJournalEditor';
import { SignCell, SignQrModal } from './SafeSign';
import { useSignBoard, type BoardRow } from './safeSignApi';
import {
  educationLabel,
  HQ,
  isInspection,
  isSheetType,
  JOURNAL_TYPES,
  educationSubsOf,
  useEducationCatalog,
  useHeadApproval,
  usePatrolTemplate,
  useSafeJournal,
  useSafeJournals,
  type ApprovalRole,
  type JournalSummary,
  type JournalType,
} from './safeJournalApi';
import { LegacyRecordModal } from '@/pages/legacy/LegacyArchivePage';

import { headCellOf } from './approvalCells';
import { SHEET_SIGNERS } from './sheetModel';
import { isDocType } from './docs/types';

/**
 * 안전 관리 › 안전교육일지 · 안전보건일지 · 순회점검일지 (DOC-122).
 *
 * <p>주소들이 이 한 화면을 종류만 바꿔 연다. 현장 화면(`/sites/:siteId/…` · DOC-135)에서는 {@code siteId} 로 현장이 고정되어
 * 고르개가 빠진다. 일지는 현장별이고 본사도 한 칸이다(사용자 결정 2026-10-20).
 * 위 줄의 현장 고르개가 목록을 거르고, 새 일지의 현장 기본값도 된다.
 *
 * <p>줄을 누르면 편집 창이 열린다. 결재는 없다 — 저장한 일지가 정본이다(DOC-122 §7).
 */
/** 현장 고르기 맨 위 — 전체 · 본사. 그 아래는 국내 진행 · 해외 진행 · 예정 · 평가 · 준공 묶음이다. */
const SITE_EXTRAS = [
  { value: '', label: '전체' },
  { value: HQ, label: '본사' },
];

export function SafeJournalPage({ type, siteId }: { type: JournalType; siteId?: string }) {
  const meta = JOURNAL_TYPES[type];
  const pageTitle = meta.label;
  const sites = useSites();
  const access = useSafeAccess();

  const [site, setSite] = useState(siteId ?? '');
  const [from, setFrom] = useState(() => monthsAgo(3));
  const [to, setTo] = useState('');
  /** 열린 편집 창. id 가 null 이면 새 일지. */
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  /** 기존 데이터에서 옮긴 일지(V612)의 원본 한 장 — 사진 · 서명 · 세부 표는 원본에 있다. */
  const [legacyOpen, setLegacyOpen] = useState<{ source: string; code: string } | null>(null);

  /**
   * `?open=<일지 id>` — 할일(결재 요청 · V550)을 누르면 그 일지가 바로 열린다. 할일 주소는 일지 종류를 몰라 교육일지
   * 주소로 오므로, 다른 종류면 그 종류의 주소로 넘긴 뒤 연다. 연 뒤에는 주소에서 지운다(새로고침에 또 열리지 않게).
   */
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const openId = params.get('open');
  const opened = useSafeJournal(openId);
  useEffect(() => {
    if (!openId || !opened.data) return;
    if (opened.data.journalType !== type) {
      navigate(`${siteId ? `/sites/${siteId}` : ''}${JOURNAL_TYPES[opened.data.journalType].path}?open=${openId}`, { replace: true });
      return;
    }
    setEditing({ id: openId });
    setParams(
      (prev) => {
        prev.delete('open');
        return prev;
      },
      { replace: true },
    );
  }, [openId, opened.data, type, siteId, navigate, setParams]);

  /** `?new=1` — 대시보드의 「미실시」 · 「미작성」 을 누르면 새 일지 창이 바로 열린다. 연 뒤 주소에서 지운다. */
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setEditing({ id: null });
    setParams(
      (prev) => {
        prev.delete('new');
        return prev;
      },
      { replace: true },
    );
  }, [wantsNew, setParams]);

  const list = useSafeJournals(type, site, from, to);
  const rows = list.data ?? [];

  // 이수확인서 · 이행각서 QR(DOC-129) — 편집 창 밖, 목록의 QR 칸에서 다룬다.
  const catalog = useEducationCatalog(type === 'EDUCATION');
  const [qrFor, setQrFor] = useState<{ id: string; title: string } | null>(null);
  const board = useSignBoard(type === 'EDUCATION' ? rows.map((row) => row.id) : [], qrFor !== null);

  return (
    <div className="space-y-5">
      <PageHeader
        title={pageTitle}
        actions={
          <div className="flex items-center gap-2">
            {/* 양식(V535 · V540 · §14 · §20) — 고치는 사람은 안전보건팀 · 최고관리자. 그 밖의 사람이 저장하면 서버가 답한다. */}
            {(type === 'PATROL' || type === 'EDUCATION') && (
              <button className="btn-secondary" onClick={() => setTemplateOpen(true)}>
                양식 편집
              </button>
            )}
            <button className="btn-primary" onClick={() => setEditing({ id: null })}>
              새 일지
            </button>
          </div>
        }
      />

      <div className="card p-4" data-tour="page-filters">
        <div className="flex items-center gap-2 flex-wrap">
          {!siteId && (
            <div style={{ width: '260px' }}>
              <SiteSearchSelect
                sites={sites.data ?? []}
                value={site}
                onChange={setSite}
                extras={SITE_EXTRAS}
                width="260px"
                className="input"
                buttonStyle={{ ...inputStyle, minHeight: '38px' }}
              />
            </div>
          )}
          <input
            type="date"
            className="input"
            style={{ ...inputStyle, width: '150px' }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="시작일"
          />
          <span style={{ color: 'var(--text-muted)' }}>~</span>
          <input
            type="date"
            className="input"
            style={{ ...inputStyle, width: '150px' }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="종료일"
          />
        </div>
      </div>

      {list.isPending ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyCard>해당하는 일지가 없습니다.</EmptyCard>
      ) : (
        <div className="card p-0" style={{ overflowX: 'auto' }} data-tour="page-list">
          <JournalTable
            type={type}
            rows={rows}
            board={board.data}
            onOpen={(row) =>
              row.legacySource && row.legacyCode ? setLegacyOpen({ source: row.legacySource, code: row.legacyCode }) : setEditing({ id: row.id })
            }
            onQr={(row) => setQrFor({ id: row.id, title: journalTitle(row, catalog.data) })}
          />
        </div>
      )}

      {/* 기존 데이터는 일지로 옮겨 위 목록에 섞여 있다(V612 · 2026-11-02) — 따로 붙이던 「기존 데이터」 칸은 걷었다. */}
      {legacyOpen && <LegacyRecordModal source={legacyOpen.source} code={legacyOpen.code} onClose={() => setLegacyOpen(null)} />}

      {qrFor && <SignQrModal journalId={qrFor.id} title={qrFor.title} onClose={() => setQrFor(null)} />}

      {templateOpen && type === 'PATROL' && <PatrolTemplateEditor onClose={() => setTemplateOpen(false)} />}
      {templateOpen && type === 'EDUCATION' && <EducationTemplateEditor onClose={() => setTemplateOpen(false)} />}
      {editing && (
        <SafeJournalEditor
          type={type}
          journalId={editing.id}
          defaultSite={site}
          // 배정 현장만 보는 사람에게는 자기 현장만 고르게 한다(DOC-136 §6 「모든 데이터는 본인 소속 현장만」).
          sites={access.all ? (sites.data ?? []) : (sites.data ?? []).filter((one) => access.siteIds.includes(one.id))}
          allowHq={access.all}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function JournalTable({
  type,
  rows,
  board,
  onOpen,
  onQr,
}: {
  type: JournalType;
  rows: JournalSummary[];
  board: BoardRow[] | undefined;
  onOpen: (row: JournalSummary) => void;
  onQr: (row: JournalSummary) => void;
}) {
  const catalog = useEducationCatalog(type === 'EDUCATION');
  const signs = new Map((board ?? []).map((row) => [row.journalId, row]));
  // 「승인」 칸 — 결재란 「현장소장」 승인(V548). 목록에서 바로 누른다. 취소는 일지를 열어 그 칸에서.
  const approve = useHeadApproval(null);
  const [approveError, setApproveError] = useState<string | null>(null);
  const patrolTemplate = usePatrolTemplate(isInspection(type));
  /** 그 일지의 결재 칸 이름 — 종이의 결재란과 같다(안전보건 고정 · 교육은 종류별 · 순회는 양식 머리). */
  /** 머리에 적는 결재 칸 이름 — 교육일지는 종류마다 첫 칸 이름이 다를 수 있어(담당 · 관리감독자) 대표 이름을 쓰고, 상자에 올리면 그 줄의 이름이 뜬다. */
  const headSigners =
    type === 'HEALTH' || isSheetType(type)
      ? SHEET_SIGNERS
      : isInspection(type)
        ? patrolTemplate.data?.header?.signers?.length
          ? patrolTemplate.data.header.signers
          : ['소장']
        : ['안전관리자', '소장'];
  const signersOf = (row: JournalSummary): string[] => {
    if (type === 'HEALTH' || isSheetType(type)) return SHEET_SIGNERS;
    if (isInspection(type)) return patrolTemplate.data?.header?.signers?.length ? patrolTemplate.data.header.signers : ['소장'];
    const sub = educationSubsOf(catalog.data, row.educationKind).find((one) => one.code === row.educationSubKind);
    return sub?.signers?.length === 2 ? sub.signers : ['안전관리자', '소장'];
  };
  const cell = { padding: '8px 12px' } as const;
  const head = { padding: '10px 12px' } as const;
  const num = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' } as const;

  return (
    <>
      <style>{APPROVAL_STYLES}</style>
      {approveError && (
        <Notice tone="warning" closable onClose={() => setApproveError(null)}>
          {approveError}
        </Notice>
      )}
    {/* 폰(앱의 기준 · DOC-140)에서는 칸을 줄이지 않고 표를 가로로 민다 — 줄이면 「작성업체」 칸이 화면 밖에서 잘린다. */}
    <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', minWidth: '560px', fontSize: '13px', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '12px' }}>
          <th style={head}>
            {type === 'EDUCATION' ? '교육일시' : isInspection(type) ? '점검일' : type === 'RISK' ? '평가일' : type === 'TBM' ? 'TBM일자' : '일자'}
          </th>
          <th style={head}>현장</th>
          {type === 'EDUCATION' && <th style={head}>교육구분</th>}
          {type === 'EDUCATION' && <th style={head}>대상</th>}
          {type === 'EDUCATION' && <th style={{ ...head, textAlign: 'right' }}>인원</th>}
          {isInspection(type) && <th style={head}>점검자</th>}
          {type === 'RISK' && <th style={head}>구분</th>}
          {type === 'RISK' && <th style={head}>평가자</th>}
          {type === 'TBM' && <th style={head}>작성업체</th>}
          {type === 'TBM' && <th style={head}>작업(공종)</th>}
          {isDocType(type) && <th style={head}>제목</th>}
          {type !== 'HEALTH' && type !== 'TBM' && !isDocType(type) && (
            <th style={head}>{type === 'EDUCATION' ? '교육내용' : type === 'RISK' ? '비고' : '특이사항 및 조치사항'}</th>
          )}
          {type !== 'EDUCATION' && !isDocType(type) && (
            <th style={{ ...head, textAlign: 'right' }}>{isInspection(type) || type === 'TBM' ? '점검 항목' : '위험요인'}</th>
          )}
          {(isInspection(type) || type === 'RISK' || type === 'TBM') && (
            <th style={{ ...head, textAlign: 'right' }}>{type === 'RISK' ? '중점관리' : type === 'TBM' ? '미조치' : '미흡'}</th>
          )}
          <th style={head}>작성자</th>
          <th style={{ ...head, width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }}>
            {/* 결재 칸 이름은 머리에 한 번 — 줄에는 체크 상자만(사용자 지시 2026-10-23 「승인 헤더를 가운데로 · 깔끔하게」). */}
            <div>승인</div>
            {headSigners.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                {headSigners.map((name) => (
                  <span key={name} style={{ width: `${APPROVAL_SLOT}px`, fontSize: '11px', fontWeight: 500 }}>
                    {name}
                  </span>
                ))}
              </div>
            )}
          </th>
          {type === 'EDUCATION' && <th style={head}>QR</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => onOpen(row)}>
            <td style={{ ...cell, whiteSpace: 'nowrap', fontWeight: 600 }}>
              {row.pinned && <span className="badge badge-blue" style={{ marginRight: '6px' }}>예시</span>}
              {row.journalDate}
              {row.startTime && <span style={{ marginLeft: '6px', fontWeight: 400, color: 'var(--text-muted)' }}>{row.startTime}</span>}
            </td>
            <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.siteId ? row.siteName ?? '—' : '본사'}</td>
            {type === 'EDUCATION' && (
              <td style={{ ...cell, maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {educationLabel(catalog.data, row.educationKind, row.educationSubKind) || '—'}
              </td>
            )}
            {type === 'EDUCATION' && <td style={cell}>{row.target ?? '—'}</td>}
            {type === 'EDUCATION' && <td style={num}>{row.headcount ?? '—'}</td>}
            {isInspection(type) && <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.inspector ?? '—'}</td>}
            {type === 'RISK' && <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.title ? `${row.title} 위험성평가` : '—'}</td>}
            {type === 'RISK' && <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.inspector ?? '—'}</td>}
            {type === 'TBM' && <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.vendorName ?? '—'}</td>}
            {type === 'TBM' && (
              <td style={{ ...cell, maxWidth: '420px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {worksText(row.workItems ?? [])}
              </td>
            )}
            {isDocType(type) && (
              <td style={{ ...cell, maxWidth: '520px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title ?? '—'}</td>
            )}
            {type !== 'HEALTH' && type !== 'TBM' && !isDocType(type) && (
              <td style={{ ...cell, maxWidth: '420px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {firstLine(row.content) ?? '—'}
              </td>
            )}
            {type !== 'EDUCATION' && !isDocType(type) && <td style={num}>{row.lineCount}</td>}
            {(isInspection(type) || type === 'RISK' || type === 'TBM') && (
              <td style={num}>
                {row.openCount > 0 ? <span className={`badge ${type === 'RISK' ? 'badge-blue' : 'badge-yellow'}`}>{row.openCount}</span> : 0}
              </td>
            )}
            <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.authorName ?? '—'}</td>
            <td style={{ ...cell, whiteSpace: 'nowrap', width: '1%' }}>
              <ApprovalBoxes
                signers={signersOf(row)}
                row={row}
                busy={approve.isPending && approve.variables?.target === row.id}
                onToggle={(role, on) => {
                  setApproveError(null);
                  approve.mutate({ target: row.id, approve: on, role }, { onError: (cause) => setApproveError(errorMessage(cause)) });
                }}
              />
            </td>
            {type === 'EDUCATION' && (
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                <SignCell row={signs.get(row.id)} onOpen={() => onQr(row)} />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
    </>
  );
}

/**
 * 목록의 「승인」 칸 — 결재란 칸마다 체크 상자 하나(사용자 지시 2026-10-23 「안전관리자, 소장 이렇게 반 나눠서 체크박스로,
 * 승인되면 초록색으로 체크」). 칸 이름은 종이의 결재란 그대로이고, 누르면 승인 · 다시 누르면 취소를 묻는다. 줄은 열지 않는다.
 */
function ApprovalBoxes({
  signers,
  row,
  busy,
  onToggle,
}: {
  signers: string[];
  row: JournalSummary;
  busy: boolean;
  onToggle: (role: ApprovalRole, on: boolean) => void;
}) {
  const head = headCellOf(signers);
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      {signers.map((name, at) => {
        const role: ApprovalRole = at === head ? 'HEAD' : 'MANAGER';
        const when = role === 'HEAD' ? row.headApprovedAt : row.managerApprovedAt;
        const who = role === 'HEAD' ? row.headApproverName : row.managerApproverName;
        const on = !!when;
        return (
          <span key={at} style={{ width: `${APPROVAL_SLOT}px`, display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              className={on ? 'sj-approve on' : 'sj-approve'}
              disabled={busy}
              title={on ? `${name} · ${who ?? ''} · ${when!.slice(5, 10).replace('-', '/')}` : `${name} 승인`}
              aria-label={`${name} 승인`}
              aria-pressed={on}
              onClick={(e) => {
                e.stopPropagation();
                if (on && !window.confirm(`${name} 승인을 취소할까요?`)) return;
                onToggle(role, !on);
              }}
            >
              {on && (
                <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
                  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </span>
        );
      })}
    </div>
  );
}

/** 승인 상자 한 칸의 폭 — 머리의 결재 칸 이름과 줄의 상자가 같은 폭으로 줄을 맞춘다. */
const APPROVAL_SLOT = 64;

/** 승인 상자 — 비었을 때는 옅은 테두리, 올리면 초록 테두리, 승인이면 초록 바탕에 흰 체크. */
const APPROVAL_STYLES = `
.sj-approve { width: 22px; height: 22px; border-radius: 6px; border: 1.5px solid #d1d5db; background: #fff; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; padding: 0; cursor: pointer; transition: all .15s; }
.sj-approve:hover:not(:disabled) { border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12); }
.sj-approve.on { background: #16a34a; border-color: #16a34a; }
.sj-approve.on:hover:not(:disabled) { background: #15803d; border-color: #15803d; }
.sj-approve:disabled { opacity: .6; cursor: default; }
`;

/** QR 창 · 관리 창의 일지 이름 — 교육일 · 현장 · 교육구분. */
function journalTitle(row: JournalSummary, catalog: Parameters<typeof educationLabel>[0]): string {
  return [row.journalDate, row.siteId ? row.siteName ?? '—' : '본사', educationLabel(catalog, row.educationKind, row.educationSubKind)]
    .filter(Boolean)
    .join(' · ');
}

function firstLine(text: string | null): string | null {
  if (!text) return null;
  return text.split('\n').find((line) => line.trim()) ?? null;
}

/** n 달 전 1일(YYYY-MM-DD). 목록의 기본 시작일 — 한 분기 남짓을 본다. */
function monthsAgo(months: number): string {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - months, 1);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${first.getFullYear()}-${pad(first.getMonth() + 1)}-01`;
}

/** TBM 작업종류 — 앞 셋과 「외 N개」(목록 한 칸). */
function worksText(works: string[]): string {
  if (works.length === 0) return '—';
  return works.length > 3 ? `${works.slice(0, 3).join(', ')} 외 ${works.length - 3}개` : works.join(', ');
}
